import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { Button } from '@deliveryhero/pd-cookbook/components/ButtonV2';
import IcEdit from '@deliveryhero/pd-cookbook/components/Icons/IcEdit';
import IcTrash from '@deliveryhero/pd-cookbook/components/Icons/IcTrash';
import { MenuItem } from '@mui/material';

import ActionMenu from 'components/ActionMenu';
import CTA from 'components/CallToActions';
import { LoadingAnimation } from 'components/LoadingAnimation';
import Page, { ActionType } from 'components/Page';
import PageHeader from 'components/PageHeader/PageHeader';
import FoodoraCard from 'components/svg/FoodoraCard';
import TableList from 'components/TableList';
import { Column } from 'components/TableList/SortableHeader';
import { RowsPerPage } from 'components/TableList/types';
import { FormikHelpers, FormikProps } from 'formik';
import useAction from 'hooks/useAction';
import { ActionOptionType } from 'hooks/useAction/useAction';
import useApi from 'hooks/useApi';
import useNotification from 'hooks/useNotification/useNotification';
import useQueryParam from 'hooks/useQueryParam/useQueryParam';
import useQueryWrapperP0 from 'hooks/useQueryWrapperP0';
import useQueryWrapperP1 from 'hooks/useQueryWrapperP1';
import { ExpenseCode, ExpenseCodePayload } from 'services/api';
import { dispatchGaEvent, PageType } from 'services/google-analytics';
import { AppRoute } from 'share/types/routes';
import AddPayment from 'src/components/svg/AddPayment';
import ExpenseCodeQueryKeys from 'src/query-keys/expense-code';
import isFoodora from 'src/utils/is-foodora/is-foodora';
import generateQuery from 'utils/generate-query';

import {
  Actions,
  add_expense_code_button,
  add_expense_code_form_button_one,
  add_expense_code_form_button_two,
  add_expense_code_form_header,
  delete_expense_code_button,
  delete_expense_code_dialog_button_one,
  delete_expense_code_dialog_button_two,
  delete_expense_code_dialog_subtitle,
  delete_expense_code_dialog_title,
  edit_expense_code_button,
  edit_expense_code_form_button,
  edit_expense_code_form_header,
  expense_code_empty_cta_subtitle,
  expense_code_empty_cta_title,
  expense_code_table_header_two,
  title
} from './constants';
import { ExpenseCodeFilters } from './Filter/ExpenseCodeFilters';
import { ExpenseCodeForm, ExpenseCodeValuesType } from './Form';

export function ExpenseCodeContainer() {
  const api = useApi();
  const navigate = useNavigate();
  const queryParam = useQueryParam();
  const [keepSidebarOpen, setSidebarOpen] = useState(false);
  const [expenseCodeToDelete, setExpenseToDelete] = useState<ExpenseCode>();
  const [, setAction, { id: id_str }] = useAction<Actions>();
  const id = parseInt(id_str);
  const queryClient = useQueryClient();
  const { pushNotification, dismissNotification } = useNotification();

  useEffect(() => {
    dispatchGaEvent({ pageType: PageType.EXPENSE_CODE });
  }, []);

  const filters = ['search', 'count', 'offset'];
  const filtered = Object.keys(queryParam)
    .filter((key) => filters.includes(key))
    .reduce((param: Record<string, string>, key) => {
      param[key] = queryParam[key];
      return param;
    }, {});

  const expenseCodes = useQueryWrapperP0(
    ExpenseCodeQueryKeys.list(filtered),
    async () => {
      return api.company.expenseCode.list(filtered);
    }
  );

  const expenseCode = useQueryWrapperP1(
    ExpenseCodeQueryKeys.detail(id),
    async () => {
      const response = await api.company.expenseCode.get(id);
      return response.data;
    },
    {
      enabled: Boolean(id)
    }
  );

  const page = queryParam.offset ? parseInt(queryParam.offset) : undefined;
  const rowsPerPage = queryParam.count ? parseInt(queryParam.count) : undefined;
  const tableListProps = {
    ...(page && {
      page: Math.floor(page / (rowsPerPage || RowsPerPage.TWENTYFIVE))
    }),
    ...(rowsPerPage && { rowsPerPage })
  };
  const columns: Column<ExpenseCode>[] = useMemo(
    () => [
      {
        id: 'name',
        options: { disablePadding: false },
        label: expense_code_table_header_two
      }
    ],
    []
  );

  const onPageChange = (
    changes: Record<string, string | number | undefined>
  ) => {
    let params = {
      ...queryParam,
      ...changes
    };

    params = Object.keys(params).reduce((acc, key) => {
      params[key] !== undefined && (acc[key] = params[key]);
      return acc;
    }, {} as Record<string, any>);

    navigate(
      {
        pathname: AppRoute.ExpenseCode,
        search: `${generateQuery(params)}`
      },
      { replace: true }
    );
  };
  const handlePaginationChange = (page: number, rowsPerPage: number) => {
    onPageChange({
      ...{
        offset: page ? page * rowsPerPage : undefined,
        count: rowsPerPage
      }
    });
  };

  const addExpenseCode = useMutation((payload: ExpenseCodePayload) =>
    api.company.expenseCode.create(payload)
  );

  const editExpenseCode = useMutation(
    (expenseCode: { id: number; payload: ExpenseCodePayload }) =>
      api.company.expenseCode.update(expenseCode.id, expenseCode.payload)
  );

  const deleteExpenseCode = useMutation((expenseCode: { id: number }) =>
    api.company.expenseCode.delete(expenseCode.id)
  );

  const handleSubmitEdit = (
    values: ExpenseCodeValuesType,
    actions: FormikHelpers<ExpenseCodeValuesType>
  ) => {
    if (id) {
      const snackbarKey = pushNotification(`Updating ${values.name}...`, {
        variant: 'info'
      });
      editExpenseCode.mutate(
        {
          id: id,
          payload: values as ExpenseCodeValuesType
        },
        {
          onSuccess: () => {
            queryClient
              .invalidateQueries(ExpenseCodeQueryKeys.list())
              .then(() => {
                pushNotification(`${values.name} updated`, {
                  variant: 'success',
                  autoHideDuration: 10000
                });
              });
            setAction(false);
          },
          onError: (e) => {
            const error = e as Error;
            const error_messge_id = pushNotification(
              `Error updating ${values.name}, ${error.message}`,
              {
                variant: 'error',
                onClick: () => {
                  actions.submitForm();
                  dismissNotification(error_messge_id);
                },
                autoHideDuration: 10000
              }
            );
          },
          onSettled: () => {
            dismissNotification(snackbarKey);
            actions.setSubmitting(false);
          }
        }
      );
    }
  };

  const handleSubmitAdd = (
    values: ExpenseCodeValuesType,
    actions: FormikHelpers<ExpenseCodeValuesType>
  ) => {
    const loading = pushNotification(`Adding ${values.name}...`, {
      variant: 'info'
    });
    addExpenseCode.mutate(values, {
      onSuccess: () => {
        queryClient.invalidateQueries(ExpenseCodeQueryKeys.list()).then(() => {
          pushNotification(`${values.name} added`, {
            variant: 'success',
            autoHideDuration: 10000
          });
          setAction(Actions.Add, keepSidebarOpen);
          actions.resetForm();
        });
      },
      onError: (e) => {
        const error = e as Error;
        const error_messge_id = pushNotification(
          `Error adding ${values.name}, ${error.message}`,
          {
            variant: 'error',
            onClick: () => {
              actions.submitForm();
              dismissNotification(error_messge_id);
            },
            autoHideDuration: 10000
          }
        );
      },
      onSettled: () => {
        dismissNotification(loading);
        actions.setSubmitting(false);
      }
    });
  };

  const handleClick = (action: Actions, options?: ActionOptionType) => {
    setAction(action, true, options);
  };

  const handleDelete = (row: ExpenseCode) => {
    setExpenseToDelete(row);
    handleClick(Actions.Delete);
  };

  const handleSubmitDelete = () => {
    if (!expenseCodeToDelete) {
      return;
    }

    const expenseCodeName = expenseCodeToDelete.name;
    const snackbarKey = pushNotification(`Deleting ${expenseCodeName}...`, {
      variant: 'info'
    });

    deleteExpenseCode.mutate(
      {
        id: expenseCodeToDelete.id
      },
      {
        onSuccess: () => {
          dismissNotification(snackbarKey);
          queryClient
            .invalidateQueries(ExpenseCodeQueryKeys.list())
            .then(() => {
              pushNotification(`${expenseCodeName} deleted`, {
                variant: 'success',
                autoHideDuration: 10000
              });
              setAction(false);
            });
        },
        onError: (e) => {
          dismissNotification(snackbarKey);

          const error = e as Error;
          const error_message_id = pushNotification(
            `We are unable to delete ${expenseCodeName}, ${error.message}`,
            {
              variant: 'error',
              onClick: () => {
                dismissNotification(error_message_id);
              }
            }
          );
        }
      }
    );
  };

  const DeleteButtons = () => (
    <>
      <Button variant="secondary">
        {delete_expense_code_dialog_button_one}
      </Button>
      <Button onClick={() => handleSubmitDelete()} marginLeft="sm">
        {delete_expense_code_dialog_button_two}
      </Button>
    </>
  );

  const detailFormRef = useRef<FormikProps<ExpenseCodeValuesType>>(null);

  return (
    <Page
      actions={{
        [Actions.Delete]: {
          component: !expenseCode.isLoading ? (
            <DeleteButtons />
          ) : (
            <LoadingAnimation />
          ),
          type: ActionType.AppDialog,
          options: {
            title: `${delete_expense_code_dialog_title} ${expenseCodeToDelete?.name}?`,
            content: delete_expense_code_dialog_subtitle
          }
        },
        [Actions.Edit]: {
          component: !expenseCode.isLoading ? (
            <ExpenseCodeForm
              initialValues={
                {
                  name: expenseCode.data?.name || ''
                } as ExpenseCodeValuesType
              }
              detailFormRef={detailFormRef}
              handleSubmit={handleSubmitEdit}
              actions={() => (
                <div style={{ width: '100%' }}>
                  <Button
                    type="submit"
                    size="small"
                    layout="full-width-primary"
                    disabled={detailFormRef.current?.values.name.length === 0}
                    onClick={() => {
                      detailFormRef.current?.handleSubmit();
                    }}
                  >
                    {edit_expense_code_form_button}
                  </Button>
                </div>
              )}
            />
          ) : (
            <LoadingAnimation />
          ),
          type: ActionType.RightDrawer,
          options: {
            title: edit_expense_code_form_header
          }
        },
        [Actions.Add]: {
          component: (
            <ExpenseCodeForm
              initialValues={
                {
                  name: ''
                } as ExpenseCodeValuesType
              }
              handleSubmit={handleSubmitAdd}
              detailFormRef={detailFormRef}
              actions={() => (
                <>
                  <Button
                    style={{ width: '160px' }}
                    variant="secondary"
                    size="small"
                    type="submit"
                    onClick={() => {
                      setSidebarOpen(true);
                      detailFormRef.current?.handleSubmit();
                    }}
                  >
                    {add_expense_code_form_button_one}
                  </Button>
                  <Button
                    type="submit"
                    size="small"
                    style={{ width: '160px' }}
                    onClick={() => {
                      setSidebarOpen(false);
                      detailFormRef.current?.handleSubmit();
                    }}
                  >
                    {add_expense_code_form_button_two}
                  </Button>
                </>
              )}
            />
          ),
          type: ActionType.RightDrawer,
          options: {
            title: add_expense_code_form_header
          }
        }
      }}
    >
      <PageHeader
        title={title}
        buttons={[
          <Button
            onClick={() => handleClick(Actions.Add)}
            key={'add-expenseCode'}
          >
            {add_expense_code_button}
          </Button>
        ]}
      />
      <TableList
        total={
          expenseCodes.error
            ? 0
            : expenseCodes.data
            ? expenseCodes.data.total
            : -1
        }
        columns={columns}
        loading={expenseCodes.isLoading}
        data={expenseCodes.data?.data || []}
        onPaginationChange={handlePaginationChange}
        expandFilters={true}
        {...tableListProps}
        action={(row: ExpenseCode) => (
          <ActionMenu>
            <MenuItem
              onClick={() =>
                handleClick(Actions.Edit, { id: row.id.toString() })
              }
              style={{ minWidth: 120, display: 'flex', textAlign: 'left' }}
            >
              <IcEdit style={{ marginRight: '15px' }} />
              {edit_expense_code_button}
            </MenuItem>
            <MenuItem
              onClick={() => handleDelete(row)}
              style={{ minWidth: 120, display: 'flex', textAlign: 'left' }}
            >
              <IcTrash style={{ marginRight: '15px' }} />
              {delete_expense_code_button}
            </MenuItem>
          </ActionMenu>
        )}
        empty={
          Object.keys(filtered).length == 0 && (
            <CTA
              illustration={isFoodora() ? FoodoraCard : AddPayment}
              title={expense_code_empty_cta_title}
              subtitle={expense_code_empty_cta_subtitle}
            >
              <Button key="1" onClick={() => handleClick(Actions.Add)}>
                {add_expense_code_button}
              </Button>
            </CTA>
          )
        }
      >
        <ExpenseCodeFilters params={queryParam} onPageChange={onPageChange} />
      </TableList>
    </Page>
  );
}
